
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SearchBar } from '@/components/SearchBar';
import { PageHero } from '@/components/PageHero';
import { RegionsSelector } from '@/components/RegionsSelector';
import { DogSchoolsList } from '@/components/DogSchoolsList';
import { CountryRegionsLinks } from '@/components/CountryRegionsLinks';
import { EducationalSection } from '@/components/EducationalSection';
import { TableOfContents } from '@/components/TableOfContents';
import { useToast } from "@/components/ui/use-toast";
import { useSchoolSearch } from '@/hooks/useSchoolSearch';
import { useStateSchools } from '@/hooks/useStateSchools';
import { getCountryByValue } from '@/services/utils/countryUtils';
import { getStateName } from '@/services/utils/stateUtils';
import { ScrollToTop } from '@/components/ScrollToTop';
import { EnhancedSEOHead } from '@/components/SEO/EnhancedSEOHead';
import { BreadcrumbSchema } from '@/components/SEO/BreadcrumbSchema';
import { GeoMetaTags } from '@/components/SEO/GeoMetaTags';

// Initial page size to improve loading performance
const INITIAL_PAGE_SIZE = 25;

const State = () => {
  const { countryId = '', stateId = '' } = useParams<{ countryId: string; stateId: string }>();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // State for search functionality
  const [isSearching, setIsSearching] = useState(false);
  const [searchLocation, setSearchLocation] = useState<string>('');
  const [userPostalCode, setUserPostalCode] = useState('');
  
  // Use the custom hook for loading schools by state
  const {
    schools: stateSchools,
    isLoading,
    error,
    hasMore,
    loadMore
  } = useStateSchools({ 
    countryId, 
    stateId,
    pageSize: INITIAL_PAGE_SIZE
  });
  
  // Use the search hook for handling searches
  const {
    filteredSchools: searchResults,
    isLoading: isSearchLoading,
    error: searchError,
    handleSearch,
    handleCountUpdate,
    schoolCount,
  } = useSchoolSearch({ 
    initialCountryName: countryId === 'germany' ? 'Germany' : 
                         countryId === 'austria' ? 'Austria' : 'Switzerland',
    initialStateName: stateId
  });
  
  // Determine which schools to display based on whether a search is active
  const displayedSchools = isSearching ? searchResults : stateSchools;
  const displayLoading = isSearching ? isSearchLoading : isLoading;
  const displayError = isSearching ? searchError : error;
  
  const handleSearchSubmit = async (postalCode: string, city: string, radius: number) => {
    setIsSearching(true);
    setUserPostalCode(postalCode);
    setSearchLocation(postalCode || city || '');
    await handleSearch(postalCode, city, radius);
  };

  const handleRegionClick = (regionValue: string) => {
    navigate(`/${countryId}/${regionValue}`);
  };
  
  // Get country data using countryId
  const countryData = getCountryByValue(countryId);
  
  // Generate SEO metadata
  const countryName = countryData?.name[language === 'de' ? 'de' : 'en'] || '';
  const stateName = stateId ? getStateName(countryId, stateId, language) : '';
  
  let seoTitle, seoDescription;
  
  if (searchLocation) {
    seoTitle = schoolCount !== undefined ? 
      `${schoolCount} Hundeschulen in ${searchLocation} | Hundetrainer & Welpenschule in der Nähe` : 
      'Hundeschulen in deiner Nähe | Hundeschulenfinder';
    seoDescription = schoolCount !== undefined ?
      `Finde die beste von ${schoolCount} Hundeschulen in ${searchLocation}! ✓ Erfahrene Hundetrainer vor Ort ✓ Welpentraining ✓ Gruppenkurse & Einzeltraining ✓ Bewertungen lesen ✓ Jetzt beste Hundeschule finden!` :
      'Finde die beste Hundeschule in deiner Nähe – schnell & unkompliziert! Vergleiche Angebote für Hundetraining, lies Bewertungen und wähle den besten Hundetrainer für deinen Vierbeiner.';
  } else {
    seoTitle = stateName && schoolCount !== undefined ? 
      `${schoolCount} Hundeschulen in ${stateName} | Hundetrainer ${countryName} in der Nähe` : 
      'Hundeschulen in deiner Nähe | Hundeschulenfinder';
    seoDescription = stateName && schoolCount !== undefined ?
      `Die ${schoolCount} besten Hundeschulen in ${stateName}, ${countryName} finden! ✓ Qualifizierte Hundetrainer ✓ Welpentraining ✓ Verhaltenstherapie ✓ Agility ✓ Vergleiche Preise & Bewertungen!` :
      'Finde die beste Hundeschule in deiner Nähe – schnell & unkompliziert! Vergleiche Angebote für Hundetraining, lies Bewertungen und wähle den besten Hundetrainer für deinen Vierbeiner.';
  }
  
  const seoKeywords = `Hundeschule ${stateName || searchLocation}, Hundetrainer ${stateName || searchLocation}, Welpenschule ${countryName}, Hundeerziehung ${stateName}, Hundetraining in der Nähe, Einzeltraining, Gruppentraining, Verhaltenstherapie Hund, Agility Training`;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <EnhancedSEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`/${countryId}/${stateId}`}
        alternateLanguages={[
          { lang: 'de', url: `/${countryId}/${stateId}` },
          { lang: 'en', url: `/en/${countryId}/${stateId}` }
        ]}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Startseite', url: '/' },
          { name: countryName, url: `/${countryId}` },
          { name: stateName, url: `/${countryId}/${stateId}` }
        ]}
      />
      <GeoMetaTags
        placename={stateName}
        region={stateId.toUpperCase()}
        country="DE"
      />
      
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <PageHero 
          countryName={countryData?.name[language === 'de' ? 'de' : 'en']}
          stateName={stateName}
          backLink={`/${countryId}`}
          backLinkText={countryData?.name[language === 'de' ? 'de' : 'en'] || ''}
          countryId={countryId}
          stateId={stateId}
          schoolCount={schoolCount}
          searchLocation={searchLocation}
        />
        
        <section className="mb-8">
          <CountryRegionsLinks countryId={countryId} />
        </section>

        <section className="mb-8">
          <SearchBar onSearch={handleSearchSubmit} isSearching={isSearchLoading} />
        </section>

        {displayError && (
          <div className="mb-8 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
            {displayError}
          </div>
        )}

        <section className="mb-8">
          <TableOfContents schools={displayedSchools} />
        </section>

        <section className="mb-16">
          <DogSchoolsList 
            schools={displayedSchools} 
            loading={displayLoading} 
            userPostalCode={userPostalCode}
            onCountUpdate={handleCountUpdate}
            isPaginated={!isSearching}
            hasMore={hasMore}
            isLoadingMore={false}
            onLoadMore={loadMore}
          />
        </section>

        <section className="mb-16">
          <CountryRegionsLinks />
        </section>

        <section className="mb-16">
          <EducationalSection />
        </section>
        
        <section className="mb-16">
          <CountryRegionsLinks alwaysShowMainCountries={true} />
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default State;
